/**
 * Demo data generators for all major app features.
 *
 * When the demo toggle is on, these generators produce realistic data
 * that looks like ~4 months of consistent app usage. Data is generated
 * in memory and merged with real data — nothing is written to localStorage.
 *
 * Controlled by the same SK_DEMO_PROFILES toggle in Settings.
 */

import type { StigmaTrackerEntry } from './appTypes';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const DAY = 86_400_000;
const HOUR = 3_600_000;

function daysAgo(d: number, h = 0): string {
  return new Date(Date.now() - d * DAY - h * HOUR).toISOString();
}

/** Seeded pseudo-random to keep demo data stable across renders. */
function seededRand(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

const rand = seededRand(42);

function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(rand() * arr.length)];
}

function randBetween(min: number, max: number): number {
  return Math.round(min + rand() * (max - min));
}

/* ------------------------------------------------------------------ */
/*  1. CHECK-IN ENTRIES (~120 days, ~4 months)                         */
/* ------------------------------------------------------------------ */

type PainIntensity = 1 | 2 | 3;

interface DemoCheckinEntry {
  timestamp: string;
  isVOC: boolean;
  copingStrategies: string[];
  painAreas?: Array<{ area: string; intensity: PainIntensity }>;
  moodLevel?: number;
  energyLevel?: number;
  painLevel?: number;
  stressLevel?: number;
  worryLevel?: number;
  feelingBlueLevel?: number;
  fatigueLevel?: number;
  sleepQuality?: number;
  [key: string]: unknown;
}

const COMMON_PAIN_AREAS = [
  'left-knee', 'right-knee', 'lower-back', 'upper-back',
  'left-shoulder', 'right-shoulder', 'left-hip', 'right-hip',
  'left-thigh', 'right-thigh', 'lower-abdomen', 'right-chest',
] as const;

const COPING_POOL = [
  'Heat', 'Rest', 'Hydration', 'Medication', 'Hot bath/shower',
  'Breathing/Grounding', 'Sleep/nap', 'Distraction (TV/music)',
  'Talking to someone', 'Gentle walk', 'Stretching/Movement',
  'Following my care plan', 'Nature/fresh air',
] as const;

let _demoCheckins: DemoCheckinEntry[] | null = null;

export function getDemoCheckins(): DemoCheckinEntry[] {
  if (_demoCheckins) return _demoCheckins;

  const entries: DemoCheckinEntry[] = [];
  const totalDays = 120;

  for (let d = totalDays; d >= 0; d--) {
    // Skip ~15% of days (realistic — nobody checks in every single day)
    if (rand() < 0.15 && d > 3) continue;

    // Simulate a crisis episode roughly every 3-4 weeks
    const isCrisisWeek = (d % 25 < 3) && d > 5;
    const isVOC = isCrisisWeek && rand() < 0.4;

    // Base levels vary by period — simulate good and bad stretches
    const phase = d > 90 ? 'settling-in' : d > 60 ? 'rough-patch' : d > 30 ? 'improving' : 'stable';
    let basePain: number, baseMood: number, baseEnergy: number;

    switch (phase) {
      case 'settling-in':
        basePain = 3; baseMood = 5; baseEnergy = 5; break;
      case 'rough-patch':
        basePain = 5; baseMood = 4; baseEnergy = 3; break;
      case 'improving':
        basePain = 3; baseMood = 6; baseEnergy = 5; break;
      case 'stable':
        basePain = 2; baseMood = 7; baseEnergy = 6; break;
    }

    if (isCrisisWeek) { basePain += 3; baseMood -= 2; baseEnergy -= 2; }

    const painLevel = Math.max(0, Math.min(10, basePain + randBetween(-2, 2)));
    const moodLevel = Math.max(1, Math.min(10, baseMood + randBetween(-1, 2)));
    const energyLevel = Math.max(1, Math.min(10, baseEnergy + randBetween(-1, 2)));
    const stressLevel = Math.max(0, Math.min(10, (10 - moodLevel) + randBetween(-1, 2)));
    const fatigueLevel = Math.max(0, Math.min(10, (10 - energyLevel) + randBetween(-1, 1)));
    const sleepQuality = Math.max(1, Math.min(10, energyLevel + randBetween(-2, 1)));

    // Pain areas — more areas during high pain
    const painAreas: Array<{ area: string; intensity: PainIntensity }> = [];
    if (painLevel >= 3) {
      const areaCount = painLevel >= 7 ? randBetween(2, 4) : randBetween(1, 2);
      const used = new Set<string>();
      for (let i = 0; i < areaCount; i++) {
        const area = pick(COMMON_PAIN_AREAS);
        if (!used.has(area)) {
          used.add(area);
          const intensity: PainIntensity = painLevel >= 7 ? (rand() < 0.5 ? 3 : 2) : (rand() < 0.6 ? 1 : 2);
          painAreas.push({ area, intensity });
        }
      }
    }

    // Coping strategies — more during higher pain
    const stratCount = painLevel >= 5 ? randBetween(2, 4) : randBetween(1, 2);
    const strats: string[] = [];
    const usedStrats = new Set<string>();
    for (let i = 0; i < stratCount; i++) {
      const s = pick(COPING_POOL);
      if (!usedStrats.has(s)) { usedStrats.add(s); strats.push(s); }
    }

    // Check-in time varies: morning to evening
    const hour = randBetween(7, 22);
    entries.push({
      timestamp: daysAgo(d, 24 - hour),
      isVOC,
      copingStrategies: strats,
      painAreas: painAreas.length > 0 ? painAreas : undefined,
      painLevel,
      moodLevel,
      energyLevel,
      stressLevel,
      fatigueLevel,
      sleepQuality,
    });
  }

  _demoCheckins = entries;
  return entries;
}

/* ------------------------------------------------------------------ */
/*  2. MEDICATIONS + LOGS                                              */
/* ------------------------------------------------------------------ */

export interface DemoMedication {
  id: number;
  name: string;
  dose: string;
  frequency: string;
  notes: string | null;
  active: boolean;
}

export interface DemoMedLog {
  id: number;
  medicationId: number;
  date: string;
  taken: boolean;
  slotIndex: number;
}

let _demoMeds: { medications: DemoMedication[]; logs: DemoMedLog[] } | null = null;

export function getDemoMedications(): { medications: DemoMedication[]; logs: DemoMedLog[] } {
  if (_demoMeds) return _demoMeds;

  const medications: DemoMedication[] = [
    { id: -1, name: 'Hydroxyurea', dose: '500mg', frequency: 'Once daily', notes: 'Take with water, evening', active: true },
    { id: -2, name: 'Folic Acid', dose: '1mg', frequency: 'Once daily', notes: 'Morning with breakfast', active: true },
    { id: -3, name: 'Ibuprofen', dose: '400mg', frequency: 'As needed', notes: 'For mild-moderate pain only', active: true },
  ];

  const logs: DemoMedLog[] = [];
  let logId = -1;

  for (let d = 120; d >= 0; d--) {
    const dateStr = new Date(Date.now() - d * DAY).toISOString().slice(0, 10);

    // Hydroxyurea: ~90% adherence
    if (rand() < 0.90) {
      logs.push({ id: logId--, medicationId: -1, date: dateStr, taken: true, slotIndex: 0 });
    } else {
      logs.push({ id: logId--, medicationId: -1, date: dateStr, taken: false, slotIndex: 0 });
    }

    // Folic acid: ~85% adherence
    if (rand() < 0.85) {
      logs.push({ id: logId--, medicationId: -2, date: dateStr, taken: true, slotIndex: 0 });
    } else {
      logs.push({ id: logId--, medicationId: -2, date: dateStr, taken: false, slotIndex: 0 });
    }

    // Ibuprofen: taken on ~30% of days (as needed)
    if (rand() < 0.30) {
      logs.push({ id: logId--, medicationId: -3, date: dateStr, taken: true, slotIndex: 0 });
    }
  }

  _demoMeds = { medications, logs };
  return _demoMeds;
}

/* ------------------------------------------------------------------ */
/*  3. GOALS                                                           */
/* ------------------------------------------------------------------ */

export interface DemoGoal {
  id: string;
  templateId: string;
  adoptedAt: string;
  completedSteps: string[];
  completedAt?: string;
  xpAwarded?: boolean;
  isCustom?: boolean;
  customTitle?: string;
  customDescription?: string;
  customSteps?: Array<{ id: string; label: string }>;
}

export function getDemoGoals(): DemoGoal[] {
  return [
    // Completed goal (~3 months ago)
    {
      id: 'demo-goal-1',
      templateId: 'goal-identity-name-it',
      adoptedAt: daysAgo(105),
      completedSteps: ['ms-1', 'ms-2', 'ms-3'],
      completedAt: daysAgo(90),
      xpAwarded: true,
    },
    // Active goal — 2 of 3 steps done
    {
      id: 'demo-goal-2',
      templateId: 'goal-bias-name-it',
      adoptedAt: daysAgo(45),
      completedSteps: ['ms-1', 'ms-2'],
    },
    // Active goal — 1 of 3 steps done (newer)
    {
      id: 'demo-goal-3',
      templateId: 'goal-advocacy-care-plan',
      adoptedAt: daysAgo(14),
      completedSteps: ['ms-1'],
    },
  ];
}

/* ------------------------------------------------------------------ */
/*  4. JOURNEYS                                                        */
/* ------------------------------------------------------------------ */

/** Returns demo active journeys (journey ID → current phase ID). */
export function getDemoJourneys(): Record<string, string> {
  return {
    'emergency-room': 'recovery',  // Completed most phases of ER journey
    'school': 'managing',           // Mid-way through school journey
  };
}

/** Returns demo completed journey resource IDs. */
export function getDemoCompletedResources(): string[] {
  return [
    // ER journey resources completed
    'er-before-checklist-1', 'er-before-checklist-2', 'er-before-checklist-3',
    'er-triage-checklist-1', 'er-triage-checklist-2',
    'er-waiting-checklist-1',
    'er-going-home-checklist-1', 'er-going-home-checklist-2',
    // School journey resources
    'school-ready-checklist-1', 'school-ready-checklist-2',
    'school-managing-checklist-1',
  ];
}

/* ------------------------------------------------------------------ */
/*  5. XP & LEVEL                                                      */
/* ------------------------------------------------------------------ */

/**
 * Returns accumulated XP for ~4 months of usage.
 * Based on: ~100 check-ins (10 XP each) + 1 completed goal (80 XP) +
 * 2 journey activations (30 XP each) + ~11 resources (10 XP each) +
 * misc first-time bonuses (~100 XP) = ~1,350 XP
 */
export function getDemoXP(): number {
  return 1350;
}

/** Returns demo rewarded event IDs to avoid double-awarding. */
export function getDemoRewardedEvents(): string[] {
  return [
    'first_checkin', 'first_event_log', 'first_goal', 'first_journey',
    'tutorial_complete', 'first_circle_post', 'first_med_log',
    'demo-goal-1-xp',
  ];
}

/* ------------------------------------------------------------------ */
/*  6. HOSPITAL PREP PROFILE                                           */
/* ------------------------------------------------------------------ */

export function getDemoPrepProfile() {
  return {
    genotype: 'HbSS',
    bloodType: 'O+',
    healthCard: '',
    allergies: 'Codeine — causes severe nausea',
    triggers: 'Cold weather, dehydration, stress, high altitude',
    baselinePain: '2-3 on most days, left knee and lower back',
    preferredHospital: 'SickKids',
    specialInstructions: 'IV access is difficult — request experienced nurse or ultrasound-guided. Do not use right arm (scarring from previous central line).',
    physicianName: 'Dr. Amara Osei',
    physicianPhone: '',
  };
}

export function getDemoPrepContacts() {
  return [
    { id: 'demo-contact-1', name: 'Mum', role: 'Primary support', phone: '' },
    { id: 'demo-contact-2', name: 'Dr. Osei', role: 'Hematologist', phone: '' },
    { id: 'demo-contact-3', name: 'Auntie Grace', role: 'Backup support', phone: '' },
  ];
}

/* ------------------------------------------------------------------ */
/*  7. CRISIS PLAN (customised from defaults)                          */
/* ------------------------------------------------------------------ */

export function getDemoCrisisPlan() {
  return [
    {
      id: 'early',
      actions: [
        'Take Ibuprofen 400mg + Tylenol 500mg',
        'Apply heat pack to knees and lower back',
        'Drink 500ml water immediately',
        'Log the start time in CareWeave',
        'Text Mum to let her know',
      ],
      medications: 'Ibuprofen 400mg · Tylenol 500mg',
      contact: 'Mum — quick text is enough',
    },
    {
      id: 'moderate',
      actions: [
        'Take Hydromorphone 2mg (prescribed)',
        'Call Mum — ask her to come or be on standby',
        'Prepare hospital bag (already packed by the door)',
        'Monitor pain every 30 min in CareWeave',
      ],
      medications: 'Hydromorphone 2mg oral · Continue heat',
      contact: 'Mum · Auntie Grace as backup',
    },
    {
      id: 'severe',
      actions: [
        'Do NOT drive — Mum or Uber to SickKids',
        'Show hospital prep profile at triage',
        'Say: "I have sickle cell. This is a vaso-occlusive crisis."',
        'Ask them to page hematology if wait exceeds 1 hour',
      ],
      medications: 'IV pain meds at hospital — do not delay going',
      contact: 'Mum · Dr. Osei on-call line',
    },
    {
      id: 'emergency',
      actions: [
        'Call 911 immediately',
        'Tell dispatcher: "Sickle cell disease — possible ACS or stroke"',
        'Unlock front door if home alone',
        'Send location to Mum via phone',
      ],
      medications: 'Emergency responders will manage — do not delay',
      contact: '911 — then Mum',
    },
  ];
}

/* ------------------------------------------------------------------ */
/*  8. EVENT LOG ENTRIES                                                */
/* ------------------------------------------------------------------ */

interface DemoEventLogEntry {
  timestamp: string;
  location: string;
  personInvolved: string[];
  experienceTags: string[];
  protectiveFactors: string[];
}

export function getDemoEventLogs(): DemoEventLogEntry[] {
  // ~4 months of stigma-tracker history, surfaced in the History timeline and
  // the Patterns "stigma across care settings" panel (grouped by location and
  // experience tag, with a support count). Spread across the window with a few
  // recent entries so the 7-day default Patterns view is not empty.
  //
  // Framing: experience tags name the harm and the forces shaping it (pain bias,
  // anti-Black racism, ableism) in youth-facing language; protective factors are
  // strengths- and resilience-based. The arc moves from harm-heavy early entries
  // toward more entries where support showed up — growth without erasing harm.
  return [
    {
      timestamp: daysAgo(108, 14),
      location: 'Emergency room',
      personInvolved: ['Triage nurse'],
      experienceTags: ['Pain dismissed', 'Drug-seeking accusation', 'Long wait'],
      protectiveFactors: ['Mum advocated for me', 'Had my care plan ready'],
    },
    {
      timestamp: daysAgo(92, 9),
      location: 'School',
      personInvolved: ['Teacher'],
      experienceTags: ['Condition doubted', 'Absence penalty'],
      protectiveFactors: ['Disability office letter', 'Spoke up for myself'],
    },
    {
      timestamp: daysAgo(76, 16),
      location: 'Emergency room',
      personInvolved: ['ER doctor'],
      experienceTags: ['Racial profiling', 'Pain underrated'],
      protectiveFactors: ['Documented it', 'Knew my rights'],
    },
    {
      timestamp: daysAgo(60, 11),
      location: 'Pharmacy',
      personInvolved: ['Pharmacist'],
      experienceTags: ['Drug-seeking accusation', 'Talked down to'],
      protectiveFactors: ['Stayed calm', 'Called my hematologist'],
    },
    {
      timestamp: daysAgo(44, 13),
      location: 'Public space',
      personInvolved: ['Stranger'],
      experienceTags: ['"You don\'t look sick"', 'Intrusive questions'],
      protectiveFactors: ['Peer community support', 'Self-compassion'],
    },
    {
      timestamp: daysAgo(30, 10),
      location: 'Clinic',
      personInvolved: ['Hematologist'],
      experienceTags: ['Felt heard', 'Care plan followed'],
      protectiveFactors: ['Brought written questions', 'Logged in CareWeave beforehand'],
    },
    {
      timestamp: daysAgo(18, 20),
      location: 'Online',
      personInvolved: ['Online stranger'],
      experienceTags: ['Disbelief', 'Demeaning comments'],
      protectiveFactors: ['Online community that gets it', 'Logged off and rested'],
    },
    {
      timestamp: daysAgo(9, 15),
      location: 'Emergency room',
      personInvolved: ['Nurse'],
      experienceTags: ['Pain taken seriously', 'Treated with respect'],
      protectiveFactors: ['Hospital prep profile', 'Hematologist was paged'],
    },
    {
      timestamp: daysAgo(5, 12),
      location: 'Clinic',
      personInvolved: ['Counsellor'],
      experienceTags: ['Felt heard', 'Validated'],
      protectiveFactors: ['Therapy', 'Named what happened'],
    },
    {
      timestamp: daysAgo(2, 18),
      location: 'School',
      personInvolved: ['Classmate'],
      experienceTags: ['Microaggression', 'Othering comment'],
      protectiveFactors: ['Friend backed me up', 'Used grounding'],
    },
  ];
}

/* ------------------------------------------------------------------ */
/*  9. STIGMA TRACKER REFLECTIONS                                       */
/* ------------------------------------------------------------------ */

// Saved reflections for the Stigma Tracker's "Your reflections" list. Chip codes
// match tagConstants.ts so each entry renders fully in the read-only review.
// Mostly complete entries (default tap → read-only view) with one in-progress to
// show a resumable draft. Framing names the forces (pain bias, anti-Black racism,
// ableism) and keeps what carried the youth through strengths-based.
export function getDemoStigmaEntries(): StigmaTrackerEntry[] {
  return [
    {
      id: 'demo-stigma-1',
      status: 'complete',
      entryMode: 'walkthrough',
      createdAt: daysAgo(76, 16),
      updatedAt: daysAgo(76, 15),
      submittedAt: daysAgo(76, 15),
      title: 'ER visit during a crisis',
      step1: {
        freeText: 'I was in so much pain and it felt like no one believed me.',
        sliderValue: 1,
        affectChips: ['erased', 'powerless'],
      },
      step2: {
        freeText: 'The doctor asked how many times I had been in "looking for medication" before even examining me.',
        people: ['Provider', 'Nurse'],
        when: 'longer_ago',
        practiceChips: ['drug-seeking', 'long-wait', 'not-listened'],
        earnshawType: 'enacted',
      },
      step3: {
        setting: ['healthcare'],
        namedForces: ['anti-black-racism', 'pain-bias'],
        connectionFollowup: 'all-together',
        connectionFollowupText: 'Being young, Black, and in pain all landed at once.',
      },
      step4: {
        carriedChips: ['knowing-rights', 'family', 'self-compassion'],
      },
      depth: {
        emotionalResponseChips: ['anger', 'powerless'],
        copingInMomentChips: ['documented', 'asked-help'],
        copingAfterwardsChips: ['talked', 'rested'],
        safetySliderValue: 2,
        disbeliefValue: 4,
        identityTiedValue: 5,
        safetyThreatValue: 3,
        namedIntersections: ['misogynoir'],
        attributionChips: ['race', 'age', 'drug-seeking'],
      },
    },
    {
      id: 'demo-stigma-2',
      status: 'complete',
      entryMode: 'walkthrough',
      createdAt: daysAgo(40, 12),
      updatedAt: daysAgo(40, 11),
      submittedAt: daysAgo(40, 11),
      title: 'Note from my teacher',
      step1: {
        freeText: 'Felt like my pain was treated as an excuse.',
        sliderValue: 2,
        affectChips: ['shamed'],
      },
      step2: {
        freeText: 'My teacher said I "use my condition to get out of things" in front of the class.',
        people: ['Teacher', 'Classmate'],
        when: 'this_week',
        practiceChips: ['condition-doubted', 'dont-look-sick'],
        earnshawType: 'enacted',
      },
      step3: {
        setting: ['school'],
        namedForces: ['ableism', 'condition-stereotypes'],
        connectionFollowup: 'some',
        connectionFollowupText: '',
      },
      step4: {
        carriedChips: ['knowing-rights', 'friend', 'community-gets-it'],
      },
      depth: null,
    },
    {
      id: 'demo-stigma-3',
      status: 'complete',
      entryMode: 'quick',
      createdAt: daysAgo(12, 9),
      updatedAt: daysAgo(12, 9),
      submittedAt: daysAgo(12, 9),
      title: 'A good clinic day',
      step1: {
        freeText: 'My hematologist actually listened and followed my care plan.',
        sliderValue: 5,
        affectChips: ['seen', 'respected'],
      },
      step2: {
        freeText: 'She paged the team, checked my plan, and asked what I needed.',
        people: ['Provider'],
        when: 'this_week',
        practiceChips: [],
        earnshawType: null,
      },
      step3: {
        setting: ['healthcare'],
        namedForces: [],
        connectionFollowup: null,
        connectionFollowupText: '',
      },
      step4: {
        carriedChips: ['care-plan', 'being-heard', 'self-compassion'],
      },
      depth: null,
    },
    {
      id: 'demo-stigma-4',
      status: 'in_progress',
      entryMode: 'walkthrough',
      createdAt: daysAgo(4, 20),
      updatedAt: daysAgo(4, 19),
      submittedAt: daysAgo(4, 19),
      title: null,
      step1: {
        freeText: 'Someone online said I was faking for attention.',
        sliderValue: 2,
        affectChips: ['angry', 'mixed'],
      },
      step2: {
        freeText: 'A stranger in the comments kept saying I "don\'t look sick".',
        people: ['Online stranger'],
        when: 'yesterday',
        practiceChips: ['dont-look-sick'],
        earnshawType: 'perceived',
      },
      step3: {
        setting: ['online'],
        namedForces: ['ableism'],
        connectionFollowup: null,
        connectionFollowupText: '',
      },
      step4: {
        carriedChips: ['online-community', 'rest'],
      },
      depth: null,
    },
  ];
}
